import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '@/App';
import { axios, API, authHdr, canOps, money } from './opsApi';
import { toast } from 'sonner';
import {
  Wallet, AlertTriangle, CheckCircle2, Settings2, ChevronRight, ArrowLeft, Scale,
} from 'lucide-react';

const Cat = ({ label, value }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-sm font-bold ${value === null || value === undefined ? 'italic text-slate-400' : 'text-[#0E2A47]'}`}>
      {value === null || value === undefined ? 'Not allocated' : money(value)}
    </div>
  </div>
);

// ---- Scoped operational budget (ops.budget.view) ----------------------------
const ScopedBudget = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/ops-budget/summary`, authHdr(token)); setData(res.data); }
      catch { setData({ locations: [] }); } finally { setLoading(false); }
    })();
  }, [token]);

  if (loading) return <div className="py-10 text-center text-sm text-slate-400" data-testid="ops-budget-loading">Loading…</div>;
  const locs = data?.locations || [];
  return (
    <div data-testid="ops-budget-scoped">
      <div className="mb-4 rounded-lg bg-blue-50 px-4 py-2 text-xs text-blue-700">
        Approved Operations location budgets within your scope. Account-wide totals and TALLY financials are not shown here.
      </div>
      {locs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-400" data-testid="ops-budget-empty">
          No approved location budget allocated in your scope.
        </div>
      ) : (
        <div className="space-y-3">
          {locs.map((l) => (
            <div key={l.service_location_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ops-budget-loc-${l.service_location_id}`}>
              <div className="mb-3 flex items-center justify-between">
                <div className="font-semibold text-[#0E2A47]">{l.location_name || l.service_location_id}</div>
                {l.has_budget === false
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500" data-testid="ops-budget-nobudget">{l.status_label || 'No location budget allocated'}</span>
                  : l.needs_review
                  ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid="ops-budget-review"><AlertTriangle size={12} />Needs review</span>
                  : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 size={12} />Approved</span>}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Cat label="Labor" value={l.labor} />
                <Cat label="Supplies" value={l.supplies} />
                <Cat label="Other Direct" value={l.other_direct_cost} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Admin allocation workspace (ops.budget.allocate + finance.full) ---------
const AllocationWorkspace = () => {
  const { token } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [sel, setSel] = useState(null);
  const [prop, setProp] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAccounts = useCallback(async () => {
    try { const res = await axios.get(`${API}/ops-budget/accounts`, authHdr(token)); setAccounts(res.data || []); } catch { setAccounts([]); }
  }, [token]);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const openAccount = async (acct) => {
    setSel(acct); setProp(null);
    try { const res = await axios.get(`${API}/ops-budget/accounts/${acct.account_number}/proposal`, authHdr(token)); setProp(res.data); }
    catch { toast.error('Could not load proposal'); }
  };

  const createDraft = async (m) => {
    setBusy(true);
    try {
      await axios.post(`${API}/ops-budget/accounts/${sel.account_number}/allocations`, {
        service_location_id: m.proposed_service_location_id, category: 'labor',
        allocated_amount: m.monthly_labor, source: 'linked_labor_line',
        labor_line_ref: { location_label: m.location_label },
      }, authHdr(token));
      toast.success(`Draft allocation created for ${m.proposed_location_name}`);
      openAccount(sel);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  const approve = async (a) => {
    setBusy(true);
    try { await axios.post(`${API}/ops-budget/allocations/${a.id}/approve`, {}, authHdr(token)); toast.success('Allocation approved'); openAccount(sel); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setBusy(false); }
  };

  if (!sel) {
    return (
      <div data-testid="ops-alloc-accounts">
        <div className="mb-4 rounded-lg bg-amber-50 px-4 py-2 text-xs text-amber-800">
          Allocation management (administrators only). Proposed mappings are suggestions and require explicit confirmation + approval. No automatic splitting.
        </div>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {accounts.map((a) => (
            <button key={a.budget_id} onClick={() => openAccount(a)} className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 last:border-0" data-testid={`ops-alloc-acct-${a.account_number}`}>
              <div>
                <div className="font-semibold text-[#0E2A47]">#{a.account_number} · {a.customer_name || a.account_name || '—'}</div>
                <div className="text-xs text-slate-500">
                  Gov. labor {money(a.governing?.labor)} · {a.coverage?.account_wide ? `${a.coverage.distinct_labor_locations} locations (account-wide)` : 'single location'}
                  · {a.approved_allocations} approved / {a.draft_allocations} draft
                </div>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const r = prop?.reconciliation;
  return (
    <div data-testid="ops-alloc-detail">
      <button onClick={() => { setSel(null); setProp(null); loadAccounts(); }} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2A47]" data-testid="ops-alloc-back"><ArrowLeft size={15} />All accounts</button>
      <h3 className="text-lg font-bold text-[#0E2A47]">#{sel.account_number} · {sel.customer_name}</h3>
      {!prop ? <div className="py-8 text-center text-sm text-slate-400">Loading proposal…</div> : (
        <>
          {/* Reconciliation */}
          <div className="my-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {['labor', 'supplies', 'other_direct_cost'].map((c) => (
              <div key={c} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`ops-alloc-recon-${c}`}>
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-400"><Scale size={11} />{c.replace(/_/g, ' ')}</div>
                <div className="mt-1 text-sm">Governing <b>{money(r?.[c]?.governing)}</b></div>
                <div className="text-xs text-emerald-600">Approved {money(r?.[c]?.approved_allocated)}</div>
                <div className="text-xs text-amber-600">Unallocated {money(r?.[c]?.unallocated_balance)}</div>
                {r?.[c]?.over_allocated && <div className="text-xs font-bold text-rose-600">Over-allocated</div>}
              </div>
            ))}
          </div>

          {/* Proposed mappings */}
          <div className="mb-2 text-sm font-bold text-[#0E2A47]">Proposed labor mappings ({prop.proposed_labor_mappings.length}) — require confirmation</div>
          <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
            {prop.proposed_labor_mappings.map((m) => {
              const saved = (prop.saved_allocations || []).find((s) => s.service_location_id === m.proposed_service_location_id && s.category === 'labor');
              return (
                <div key={m.location_label} className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5 last:border-0" data-testid={`ops-alloc-map-${m.proposed_service_location_id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-[#0E2A47]">{m.location_label} → {m.proposed_location_name}</div>
                    <div className="text-xs text-slate-500">{money(m.monthly_labor)} · {m.match_confidence}</div>
                  </div>
                  {saved ? (
                    saved.status === 'approved'
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Approved</span>
                      : <button disabled={busy} onClick={() => approve(saved)} className="rounded-lg bg-[#0E2A47] px-3 py-1 text-xs font-semibold text-white disabled:opacity-50" data-testid={`ops-alloc-approve-${saved.id}`}>Approve</button>
                  ) : (
                    <button disabled={busy} onClick={() => createDraft(m)} className="rounded-lg border border-[#0E2A47] px-3 py-1 text-xs font-semibold text-[#0E2A47] disabled:opacity-50" data-testid={`ops-alloc-confirm-${m.proposed_service_location_id}`}>Confirm mapping</button>
                  )}
                </div>
              );
            })}
          </div>

          {/* SVB coverage confirmation lists */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 text-xs">
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="ops-alloc-unmatched">
              <div className="mb-1 font-bold text-amber-700">Unmatched labor ({prop.unmatched_or_unassigned.length})</div>
              {prop.unmatched_or_unassigned.map((u, i) => <div key={i} className="text-slate-600">{u.location_label} · {money(u.monthly)}</div>)}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="ops-alloc-unbudgeted">
              <div className="mb-1 font-bold text-slate-500">Unbudgeted locations ({prop.unbudgeted_service_locations.length})</div>
              {prop.unbudgeted_service_locations.slice(0, 12).map((u) => <div key={u.service_location_id} className="text-slate-600">{u.location_name}{u.is_active ? '' : ' (inactive)'}</div>)}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="ops-alloc-inactive">
              <div className="mb-1 font-bold text-slate-500">Inactive locations ({prop.inactive_service_locations.length})</div>
              {prop.inactive_service_locations.map((u) => <div key={u.service_location_id} className="text-slate-600">{u.location_name}</div>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function OpsBudget() {
  const { user } = useContext(AuthContext);
  const isAdmin = canOps(user, 'ops.budget.allocate');
  const [view, setView] = useState('scoped');

  return (
    <div className="p-6" data-testid="ops-budget">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold text-[#0E2A47]"><Wallet size={22} />Operations Budget</h2>
          <p className="text-sm text-slate-500">Approved per-location operational budgets.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button onClick={() => setView('scoped')} className={`rounded-full px-4 py-2 text-sm font-semibold ${view === 'scoped' ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="ops-budget-view-scoped">My budgets</button>
            <button onClick={() => setView('admin')} className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold ${view === 'admin' ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600'}`} data-testid="ops-budget-view-admin"><Settings2 size={14} />Allocation workspace</button>
          </div>
        )}
      </div>
      {view === 'admin' && isAdmin ? <AllocationWorkspace /> : <ScopedBudget />}
    </div>
  );
}

export default OpsBudget;
